import { delay } from 'es-toolkit/promise';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTRPC } from '../../../hooks/useTRPC';
import {
  ANDROID_PACKAGE_NAME_REGEX,
  getPackageNameFormatError,
} from '../../../lib/schemas/formSchemas';
import { captureAndReportError } from '../../../utils/errorReporting';

const MINIMUM_SPINNER_TIME_MS = 800;
const SPINNER_HIDE_DELAY_MS = 100;
const PACKAGE_NAME_DEBOUNCE_MS = 500;

export function usePackageNameAvailability(packageName: string) {
  const trpc = useTRPC();
  const [packageNameError, setPackageNameError] = useState<string | null>(null);
  const [isCheckingPackageName, setIsCheckingPackageName] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [packageNameValid, setPackageNameValid] = useState(false);
  const [checkedPackageName, setCheckedPackageName] = useState('');
  const currentPackageName = useRef(packageName);
  currentPackageName.current = packageName;
  const spinnerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkPackageNameAvailability = useCallback(
    async (nextPackageName: string) => {
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
        spinnerTimeoutRef.current = null;
      }

      if (nextPackageName.length === 0) {
        setPackageNameError(null);
        setIsCheckingPackageName(false);
        setShowSpinner(false);
        setPackageNameValid(false);
        return;
      }

      if (!ANDROID_PACKAGE_NAME_REGEX.test(nextPackageName)) {
        setPackageNameError(getPackageNameFormatError());
        setIsCheckingPackageName(false);
        setShowSpinner(false);
        setPackageNameValid(false);
        return;
      }

      setIsCheckingPackageName(true);
      setShowSpinner(true);
      setPackageNameError(null);
      setPackageNameValid(false);

      const startTime = Date.now();

      try {
        const result = await trpc.checkPackageNameAvailability.query({
          packageName: nextPackageName,
        });
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(
          0,
          MINIMUM_SPINNER_TIME_MS - elapsedTime,
        );

        if (remainingTime > 0) {
          await delay(remainingTime);
        }

        if (currentPackageName.current !== nextPackageName) return;
        setCheckedPackageName(nextPackageName);

        if ('_tag' in result && result._tag === 'Right') {
          const { available, reason } = result.right;

          if (!available) {
            if (reason === 'exists_in_database') {
              setPackageNameError(
                'This package name is already taken by another dapp',
              );
            } else if (reason === 'exists_in_external_registry') {
              setPackageNameError(
                'This package name already exists in the Solana dApp registry',
              );
            }
            setPackageNameValid(false);
            return;
          }

          setPackageNameError(null);
          setPackageNameValid(true);
        }
      } catch (error) {
        if (currentPackageName.current !== nextPackageName) return;
        captureAndReportError('Error checking package name:', error);
        setPackageNameValid(false);
      } finally {
        if (currentPackageName.current === nextPackageName) {
          setIsCheckingPackageName(false);
          spinnerTimeoutRef.current = setTimeout(() => {
            setShowSpinner(false);
          }, SPINNER_HIDE_DELAY_MS);
        }
      }
    },
    [trpc],
  );

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      void checkPackageNameAvailability(packageName);
    }, PACKAGE_NAME_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [packageName, checkPackageNameAvailability]);

  useEffect(() => {
    return () => {
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    };
  }, []);

  return {
    packageNameError,
    isCheckingPackageName,
    showSpinner,
    packageNameValid: packageNameValid && checkedPackageName === packageName,
  };
}
